// src/pages/EditUnitPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { getUnitDetails, updateUnit } from "../api/unitApi";
import { getApiErrorMessage } from "../utils/concurrency";
import DocumentManager from "../components/documents/DocumentManager";
import { getAddressById, getPropertyById, lookupAddress } from "../api/propertyApi";

import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  FormControlLabel,
  Switch,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const formatServiceCharge = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const mapPropertyAddressToUnitAddress = (property) => ({
  address_line1: property?.address_line1 || "",
  address_line2: property?.address_line2 || "",
  city: property?.city || "",
  county: property?.county || "",
  postcode: property?.postcode || "",
  ideal_postcodes_id: property?.ideal_postcodes_id || "",
  uprn: property?.uprn || "",
  address_latitude: property?.address_latitude ?? "",
  address_longitude: property?.address_longitude ?? "",
});

function EditUnitPage() {
  const { unitId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    unit_code: "",
    unit_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    county: "",
    postcode: "",
    ideal_postcodes_id: "",
    uprn: "",
    address_latitude: "",
    address_longitude: "",
    correspondence_address_line1: "",
    correspondence_address_line2: "",
    correspondence_city: "",
    correspondence_county: "",
    correspondence_postcode: "",
    correspondence_ideal_postcodes_id: "",
    correspondence_uprn: "",
    correspondence_address_latitude: "",
    correspondence_address_longitude: "",
    has_different_correspondence_address: false,
    floor: "",
    square_feet: "",
    service_charge: "",
    owner_name: "",
    mobile: "",
    email: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [rowVersion, setRowVersion] = useState(1);
  const [resolvedPropertyId, setResolvedPropertyId] = useState("");
  const [propertyAddress, setPropertyAddress] = useState(null);
  const [propertyAddressLoading, setPropertyAddressLoading] = useState(false);
  const [unitLookupPostcode, setUnitLookupPostcode] = useState("");
  const [unitLookupFilter, setUnitLookupFilter] = useState("");
  const [unitLookupResults, setUnitLookupResults] = useState([]);
  const [unitLookupLoading, setUnitLookupLoading] = useState(false);
  const [unitLookupError, setUnitLookupError] = useState("");
  const [unitSelectedLookup, setUnitSelectedLookup] = useState("");
  const [corrLookupPostcode, setCorrLookupPostcode] = useState("");
  const [corrLookupFilter, setCorrLookupFilter] = useState("");
  const [corrLookupResults, setCorrLookupResults] = useState([]);
  const [corrLookupLoading, setCorrLookupLoading] = useState(false);
  const [corrLookupError, setCorrLookupError] = useState("");
  const [corrSelectedLookup, setCorrSelectedLookup] = useState("");

  useEffect(() => {
    const loadUnit = async () => {
      try {
        const data = await getUnitDetails(unitId);
        const unit = data?.unit || {};
        const nextPropertyId = unit.property_id || "";
        setResolvedPropertyId(nextPropertyId);
        setForm({
          unit_code: unit.unit_code || "",
          unit_name: unit.unit_name || "",
          address_line1: unit.address_line1 || "",
          address_line2: unit.address_line2 || "",
          city: unit.city || "",
          county: unit.county || "",
          postcode: unit.postcode || "",
          ideal_postcodes_id: unit.ideal_postcodes_id || "",
          uprn: unit.uprn || "",
          address_latitude: unit.address_latitude ?? "",
          address_longitude: unit.address_longitude ?? "",
          correspondence_address_line1: unit.correspondence_address_line1 || "",
          correspondence_address_line2: unit.correspondence_address_line2 || "",
          correspondence_city: unit.correspondence_city || "",
          correspondence_county: unit.correspondence_county || "",
          correspondence_postcode: unit.correspondence_postcode || "",
          correspondence_ideal_postcodes_id: unit.correspondence_ideal_postcodes_id || "",
          correspondence_uprn: unit.correspondence_uprn || "",
          correspondence_address_latitude: unit.correspondence_address_latitude ?? "",
          correspondence_address_longitude: unit.correspondence_address_longitude ?? "",
          has_different_correspondence_address:
            unit.has_different_correspondence_address || false,
          floor: unit.floor || "",
          square_feet: unit.square_feet ? String(unit.square_feet) : "",
          service_charge: formatServiceCharge(unit.service_charge),
          owner_name: unit.owner_name || "",
          mobile: unit.mobile || "",
          email: unit.email || "",
        });
        setRowVersion(unit.row_version || 1);

        if (nextPropertyId) {
          setPropertyAddressLoading(true);
          try {
            const property = await getPropertyById(nextPropertyId);
            setPropertyAddress(mapPropertyAddressToUnitAddress(property));
          } catch {
            setPropertyAddress(null);
          } finally {
            setPropertyAddressLoading(false);
          }
        } else {
          setPropertyAddress(null);
          setPropertyAddressLoading(false);
        }
      } catch (err) {
        setError("Unable to load unit details.");
      } finally {
        setLoading(false);
      }
    };

    loadUnit();
  }, [unitId]);

  const handleCopyAddressFromProperty = () => {
    if (!propertyAddress) return;
    setForm((prev) => ({
      ...prev,
      ...propertyAddress,
    }));
    setUnitLookupPostcode(propertyAddress.postcode || "");
    setUnitLookupResults([]);
    setUnitSelectedLookup("");
    setUnitLookupError("");
    setToast({
      open: true,
      message: "Property address copied to unit address.",
      severity: "success",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "square_feet"
          ? value.replace(/[^\d]/g, "")
          : value,
    }));
  };

  const handleCorrespondenceToggle = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      has_different_correspondence_address: checked,
      ...(checked
        ? {}
        : {
            correspondence_address_line1: "",
            correspondence_address_line2: "",
            correspondence_city: "",
            correspondence_county: "",
            correspondence_postcode: "",
            correspondence_ideal_postcodes_id: "",
            correspondence_uprn: "",
            correspondence_address_latitude: "",
            correspondence_address_longitude: "",
          }),
    }));
    if (!checked) {
      setCorrLookupResults([]);
      setCorrSelectedLookup("");
      setCorrLookupError("");
    }
  };

  const handleUnitLookup = async () => {
    setUnitLookupError("");
    setUnitLookupResults([]);
    setUnitSelectedLookup("");
    const trimmed = unitLookupPostcode.trim();
    if (!trimmed) {
      setUnitLookupError("Enter a postcode to search.");
      return;
    }
    setUnitLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, unitLookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setUnitLookupResults(suggestions);
      if (!suggestions.length) {
        setUnitLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setUnitLookupError(err?.message || "Address lookup failed.");
    } finally {
      setUnitLookupLoading(false);
    }
  };

  const handleUnitSelectLookup = async (value) => {
    setUnitSelectedLookup(value);
    setUnitLookupError("");
    const selected = unitLookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      address_line1: "",
      address_line2: "",
      city: "",
      county: "",
      postcode: "",
      ideal_postcodes_id: "",
      uprn: "",
      address_latitude: "",
      address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        address_line1: address.line_1 || prev.address_line1,
        address_line2: address.line_2 || prev.address_line2,
        city: address.town_or_city || address.locality || prev.city,
        county: address.county || prev.county,
        postcode: address.postcode || unitLookupPostcode.trim() || prev.postcode,
        ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        uprn: address.uprn || "",
        address_latitude: address.latitude ?? "",
        address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setUnitLookupError(err?.message || "Address lookup failed.");
    }
  };

  const handleCorrLookup = async () => {
    if (!form.has_different_correspondence_address) {
      return;
    }
    setCorrLookupError("");
    setCorrLookupResults([]);
    setCorrSelectedLookup("");
    const trimmed = corrLookupPostcode.trim();
    if (!trimmed) {
      setCorrLookupError("Enter a postcode to search.");
      return;
    }
    setCorrLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, corrLookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setCorrLookupResults(suggestions);
      if (!suggestions.length) {
        setCorrLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setCorrLookupError(err?.message || "Address lookup failed.");
    } finally {
      setCorrLookupLoading(false);
    }
  };

  const handleCorrSelectLookup = async (value) => {
    setCorrSelectedLookup(value);
    setCorrLookupError("");
    const selected = corrLookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      correspondence_address_line1: "",
      correspondence_address_line2: "",
      correspondence_city: "",
      correspondence_county: "",
      correspondence_postcode: "",
      correspondence_ideal_postcodes_id: "",
      correspondence_uprn: "",
      correspondence_address_latitude: "",
      correspondence_address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        correspondence_address_line1:
          address.line_1 || prev.correspondence_address_line1,
        correspondence_address_line2:
          address.line_2 || prev.correspondence_address_line2,
        correspondence_city:
          address.town_or_city || address.locality || prev.correspondence_city,
        correspondence_county:
          address.county || prev.correspondence_county,
        correspondence_postcode:
          address.postcode ||
          corrLookupPostcode.trim() ||
          prev.correspondence_postcode,
        correspondence_ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        correspondence_uprn: address.uprn || "",
        correspondence_address_latitude: address.latitude ?? "",
        correspondence_address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setCorrLookupError(err?.message || "Address lookup failed.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        unit_code: form.unit_code,
        unit_name: form.unit_name,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        county: form.county,
        postcode: form.postcode,
        ideal_postcodes_id: form.ideal_postcodes_id || null,
        uprn: form.uprn || null,
        address_latitude: form.address_latitude || null,
        address_longitude: form.address_longitude || null,
        correspondence_address_line1: form.correspondence_address_line1,
        correspondence_address_line2: form.correspondence_address_line2,
        correspondence_city: form.correspondence_city,
        correspondence_county: form.correspondence_county,
        correspondence_postcode: form.correspondence_postcode,
        correspondence_ideal_postcodes_id:
          form.correspondence_ideal_postcodes_id || null,
        correspondence_uprn: form.correspondence_uprn || null,
        correspondence_address_latitude:
          form.correspondence_address_latitude || null,
        correspondence_address_longitude:
          form.correspondence_address_longitude || null,
        has_different_correspondence_address:
          form.has_different_correspondence_address,
        floor: form.floor,
        square_feet: form.square_feet ? Number(form.square_feet) : null,
        row_version: rowVersion,
      };

      const result = await updateUnit(unitId, payload);
      if (result?.row_version) {
        setRowVersion(result.row_version);
      }
      navigate(`/units/${unitId}/details`, {
        state: {
          toast: {
            message: "Unit updated",
            severity: "success",
          },
        },
      });
    } catch (err) {
      setToast({
        open: true,
        message: getApiErrorMessage(err, "Failed to update unit. Please try again."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box className="page-container" sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 1 }}>Loading unit details...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        className="page-container"
        sx={{ display: "flex", justifyContent: "center", mt: 4 }}
      >
        <Box sx={{ width: "100%", maxWidth: 600 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Edit Unit
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 4, borderRadius: 2 }} elevation={2}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Unit Code"
                name="unit_code"
                value={form.unit_code}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                required
                label="Unit Name"
                name="unit_name"
                value={form.unit_name}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
                inputProps={{ maxLength: 255 }}
              />

              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Unit Address
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={handleCopyAddressFromProperty}
                    disabled={saving || propertyAddressLoading || !propertyAddress}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {propertyAddressLoading
                      ? "Loading property address..."
                      : "Copy Address From Property"}
                  </Button>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Postcode"
                      value={unitLookupPostcode}
                      onChange={(e) => setUnitLookupPostcode(e.target.value)}
                      disabled={saving || unitLookupLoading}
                      fullWidth
                    />
                    <TextField
                      label="Filter (optional)"
                      value={unitLookupFilter}
                      onChange={(e) => setUnitLookupFilter(e.target.value)}
                      disabled={saving || unitLookupLoading}
                      fullWidth
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={handleUnitLookup}
                    disabled={saving || unitLookupLoading}
                    startIcon={<SearchIcon />}
                    sx={{ alignSelf: "flex-start", minWidth: 180 }}
                  >
                    {unitLookupLoading ? "Searching..." : "Find Address"}
                  </Button>
                  {unitLookupError && (
                    <Alert severity="error">{unitLookupError}</Alert>
                  )}
                  <Collapse in={unitLookupResults.length > 0} timeout={300}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {unitLookupResults.length}{" "}
                      {unitLookupResults.length === 1 ? "address" : "addresses"}{" "}
                      found. Choose one from the dropdown.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select Address</InputLabel>
                      <Select
                        value={unitSelectedLookup}
                        label="Select Address"
                        onChange={(e) => handleUnitSelectLookup(e.target.value)}
                      >
                        {unitLookupResults.map((addr, index) => {
                          const label = addr.address || [addr.id].filter(Boolean).join(", ");
                          return (
                            <MenuItem key={`${index}-${label}`} value={String(index)}>
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Collapse>
                  {[
                    { label: "Address Line 1", name: "address_line1" },
                    { label: "Address Line 2", name: "address_line2" },
                    { label: "City", name: "city" },
                    { label: "County", name: "county" },
                    { label: "Postcode", name: "postcode" },
                  ].map(({ label, name }) => (
                    <TextField
                      key={name}
                      fullWidth
                      label={label}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      margin="normal"
                      disabled={saving}
                      inputProps={{ maxLength: name === "postcode" ? 20 : 255 }}
                    />
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Correspondence Address
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.has_different_correspondence_address}
                      onChange={handleCorrespondenceToggle}
                      disabled={saving}
                    />
                  }
                  label="Has a different correspondence address"
                  sx={{ mb: 1 }}
                />
                {form.has_different_correspondence_address && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Postcode"
                      value={corrLookupPostcode}
                      onChange={(e) => setCorrLookupPostcode(e.target.value)}
                      disabled={saving || corrLookupLoading}
                      fullWidth
                    />
                    <TextField
                      label="Filter (optional)"
                      value={corrLookupFilter}
                      onChange={(e) => setCorrLookupFilter(e.target.value)}
                      disabled={saving || corrLookupLoading}
                      fullWidth
                    />
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={handleCorrLookup}
                    disabled={saving || corrLookupLoading}
                    startIcon={<SearchIcon />}
                    sx={{ alignSelf: "flex-start", minWidth: 180 }}
                  >
                    {corrLookupLoading ? "Searching..." : "Find Address"}
                  </Button>
                  {corrLookupError && (
                    <Alert severity="error">{corrLookupError}</Alert>
                  )}
                  <Collapse in={corrLookupResults.length > 0} timeout={300}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {corrLookupResults.length}{" "}
                      {corrLookupResults.length === 1 ? "address" : "addresses"}{" "}
                      found. Choose one from the dropdown.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select Address</InputLabel>
                      <Select
                        value={corrSelectedLookup}
                        label="Select Address"
                        onChange={(e) => handleCorrSelectLookup(e.target.value)}
                      >
                        {corrLookupResults.map((addr, index) => {
                          const label = addr.address || [addr.id].filter(Boolean).join(", ");
                          return (
                            <MenuItem key={`${index}-${label}`} value={String(index)}>
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Collapse>
                  {[
                    {
                      label: "Address Line 1",
                      name: "correspondence_address_line1",
                    },
                    {
                      label: "Address Line 2",
                      name: "correspondence_address_line2",
                    },
                    { label: "City", name: "correspondence_city" },
                    { label: "County", name: "correspondence_county" },
                    { label: "Postcode", name: "correspondence_postcode" },
                  ].map(({ label, name }) => (
                    <TextField
                      key={name}
                      fullWidth
                      label={label}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      margin="normal"
                      disabled={saving}
                      inputProps={{ maxLength: name.endsWith("postcode") ? 20 : 255 }}
                    />
                  ))}
                </Stack>
                )}
              </Paper>

              <TextField
                fullWidth
                label="Floor"
                name="floor"
                value={form.floor}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                label="Square Feet"
                name="square_feet"
                value={form.square_feet}
                onChange={handleChange}
                margin="normal"
                inputMode="numeric"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Service Charge"
                name="service_charge"
                value={form.service_charge}
                margin="normal"
                disabled
                helperText="Set by finance recalculation on the property finance page."
              />

              <TextField
                fullWidth
                label="Owner Name"
                name="owner_name"
                value={form.owner_name}
                onChange={handleChange}
                margin="normal"
                disabled
                inputProps={{ maxLength: 500 }}
                helperText="Change ownership from the Ownership tab."
              />

              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                margin="normal"
                disabled
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                margin="normal"
                disabled
                inputProps={{ maxLength: 255 }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  mt: 3,
                  py: 1.2,
                  fontWeight: 600,
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </Paper>

          <DocumentManager
            propertyId={resolvedPropertyId}
            targetType="unit"
            targetId={unitId}
          />
        </Box>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default EditUnitPage;
